import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  MdCheckCircleOutline,
  MdErrorOutline,
  MdWarningAmber,
  MdInfoOutline,
  MdClose,
} from "react-icons/md";
import {
  getToastAction,
  releaseToastAction,
} from "../../store/common/snackBarSlice";

import "./Toast.css";
import { useTranslation } from "react-i18next";

const ICONS = {
  success: MdCheckCircleOutline,
  error: MdErrorOutline,
  warning: MdWarningAmber,
  info: MdInfoOutline,
};

// Matches bolt-toast-out. A timer rather than animationend, which never fires
// when prefers-reduced-motion turns the animation off.
const EXIT_MS = 180;

/**
 * One notification from the snackBar queue. Rendered only by CustomSnackbar;
 * call sites go through useSnackBar(). Screen-reader announcements come from
 * CustomSnackbar's live regions, not from this element.
 */
const Toast = ({ toast, paused, onDismiss }) => {
  const { t } = useTranslation("common");
  const { id, severity, title, text, action, duration } = toast;
  const [closing, setClosing] = useState(false);
  const remaining = useRef(duration);

  const close = useCallback(() => setClosing(true), []);

  useEffect(() => () => releaseToastAction(id), [id]);

  // Counts down only while nobody is reading: hover, focus or a background
  // tab pauses it, and it resumes with the time that was left. performance
  // clock, so a system clock change can't skew it.
  useEffect(() => {
    if (!duration || paused || closing) return undefined;
    const startedAt = performance.now();
    const timer = setTimeout(close, remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current -= performance.now() - startedAt;
    };
  }, [duration, paused, closing, close]);

  useEffect(() => {
    if (!closing) return undefined;
    const timer = setTimeout(() => onDismiss(id), EXIT_MS);
    return () => clearTimeout(timer);
  }, [closing, id, onDismiss]);

  const handleKeyDown = (e) => {
    if (e.key !== "Escape") return;
    // Keep Esc from also closing a modal open behind the toast.
    e.stopPropagation();
    close();
  };

  const onAction = action && getToastAction(id);
  const Icon = ICONS[severity] || ICONS.info;

  return (
    <li
      className={`bolt-toast bolt-toast--${severity}`}
      data-state={closing ? "closing" : "open"}
      onKeyDown={handleKeyDown}
    >
      <Icon className="bolt-toast__icon" aria-hidden="true" />
      <div className="bolt-toast__body">
        <div className="bolt-toast__title">{title || text}</div>
        {title && text && <div className="bolt-toast__message">{text}</div>}
        {onAction && (
          <button
            type="button"
            className="bolt-toast__action"
            onClick={() => {
              onAction();
              close();
            }}
          >
            {action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        className="bolt-toast__close"
        onClick={close}
        aria-label={t("notifications.dismiss")}
      >
        <MdClose aria-hidden="true" />
      </button>
    </li>
  );
};

export default Toast;
